// `/embed/live/$slug` — the PUBLIC member-viewer document (#87 / ADR 0020). A
// thin primary adapter. It lives under `/embed`, NOT `/_authenticated`, so it
// bypasses the session/tenancy middleware by route-tree placement — Members are
// not Manager-authenticated in V1, and the iframe a Club frames must be public.
//
// The gate is a default-deny SSR check, mirroring the `/live-stream` route's
// discipline but HARD-HIDING (404) rather than redirecting — a Member has no
// console to fall back to. All server work — slug resolution, the `live-stream`
// flag eval, the per-Tenant `frame-ancestors` allowlist read, and setting the
// CSP header — runs inside a `createServerFn`: that is the one place a route
// file may reach server context + server-only imports (the Start
// import-protection plugin blocks them from the client-reachable loader). The
// loader calls it and throws `notFound()` on any denial. Unknown slug and
// dark/absent/errored flag both collapse to the SAME 404, so the flag state is
// not observable from the URL.

import { createFileRoute, notFound } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';

import { MemberViewerPage } from '~/bounded-contexts/streaming/ui/viewer/member-viewer-page';

interface EmbedGateResult {
	ok: boolean;
	clubName: string;
}

const DENIED: EmbedGateResult = { ok: false, clubName: '' };

// Resolve + flag-gate + set the CSP `frame-ancestors` header, all in one
// server-context call. Reuses the public-api primitives (the deliberate crossing
// of ADR 0007's public/private split — a public route reaching the public-api
// allowlist KV from inside the app tree). Flag eval is default-deny: any throw
// or a dark/absent flag denies, indistinguishably from an unknown slug. The CSP
// header is set HERE (not in the loader) because the loader is client-reachable
// and may not import the server-only header API; during SSR this runs in the
// document request context, so the header lands on the embed document.
const gateEmbedServerFn = createServerFn({ method: 'GET' })
	.inputValidator((slug: string) => slug)
	.handler(async ({ data: slug }): Promise<EmbedGateResult> => {
		const { getDb } = await import('~/_shared/db/composition');
		const { resolvePublicClub } = await import(
			'~/_shared/public-api/resolve-club-slug'
		);
		const { getServerFeatureFlags } = await import(
			'~/_shared/feature-flags/composition.server'
		);
		const { readAllowlist } = await import(
			'~/_shared/public-api/cors/allowlist'
		);
		const { getCorsAllowlistKv } = await import(
			'~/_shared/public-api/cors/allowlist-binding.server'
		);
		const { buildFrameAncestorsHeader } = await import(
			'~/_shared/public-api/cors/frame-ancestors'
		);
		const { getRequest, setResponseHeader } = await import(
			'@tanstack/react-start/server'
		);

		const club = await resolvePublicClub(getDb(), slug);
		if (!club) return DENIED;

		const flags = await getServerFeatureFlags();
		let enabled = false;
		try {
			enabled = await flags.getBoolean('live-stream', false, {
				clubId: club.id,
			});
		} catch {
			enabled = false;
		}
		if (!enabled) return DENIED;

		// The header names only the origin doing the framing, never the whole
		// allowlist — see `frame-ancestors.ts` for why enumerating it was a leak
		// and why deciding this from `Referer` is still safe.
		const origins = await readAllowlist(getCorsAllowlistKv(), club.id);
		setResponseHeader(
			'content-security-policy',
			buildFrameAncestorsHeader(origins, getRequest().headers.get('referer')),
		);
		return { ok: true, clubName: club.name };
	});

export const Route = createFileRoute('/embed/live/$slug')({
	loader: async ({ params }) => {
		const gate = await gateEmbedServerFn({ data: params.slug });
		if (!gate.ok) throw notFound();
		return { clubName: gate.clubName };
	},
	component: MemberViewerRoute,
});

function MemberViewerRoute() {
	const { slug } = Route.useParams();
	const { clubName } = Route.useLoaderData();
	return <MemberViewerPage clubSlug={slug} clubName={clubName} />;
}
